import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

const BreadcrumbContext = createContext();

export const useBreadcrumb = () => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumb must be used within a BreadcrumbProvider');
  }
  return context;
};

export const BreadcrumbProvider = ({ children }) => {
  const [customBreadcrumbs, setCustomBreadcrumbs] = useState({});
  const location = useLocation();

  // Clear custom breadcrumbs when route changes
  useEffect(() => {
    setCustomBreadcrumbs({});
  }, [location.pathname]);

  // Set custom breadcrumbs for a specific path
  const setBreadcrumbs = (path, breadcrumbs) => {
    setCustomBreadcrumbs(prev => ({
      ...prev,
      [path]: breadcrumbs
    }));
  };

  // Get breadcrumbs for current path
  const getBreadcrumbs = (path = location.pathname) => {
    return customBreadcrumbs[path] || [];
  };

  // Update page title and breadcrumbs
  const setPageMeta = (meta) => {
    const { title, breadcrumbs } = meta;
    
    if (title) {
      document.title = `${title} | Bootminds Elite`;
    }
    
    if (breadcrumbs) {
      setBreadcrumbs(location.pathname, breadcrumbs);
    }
  };

  const value = useMemo(() => ({
    setBreadcrumbs,
    getBreadcrumbs,
    setPageMeta,
    customBreadcrumbs
  }), [customBreadcrumbs]);

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

// Hook for setting page metadata including breadcrumbs
export const usePageMeta = (meta) => {
  const { setPageMeta } = useBreadcrumb();

  // Depend on serialized CONTENT, not object identity. Callers commonly pass a
  // fresh `{ title, breadcrumbs }` literal every render; keying the effect on the
  // object reference would re-run setState on every render and loop forever
  // ("Maximum update depth exceeded"). title + a serialized breadcrumb key only
  // change when the content actually changes.
  const title = meta?.title;
  const crumbsKey = meta?.breadcrumbs ? JSON.stringify(meta.breadcrumbs) : '';

  useEffect(() => {
    if (meta) {
      setPageMeta(meta);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, crumbsKey]);
};

// Hook for setting breadcrumbs only
export const useBreadcrumbItems = (breadcrumbs) => {
  const { setBreadcrumbs } = useBreadcrumb();
  const location = useLocation();
  
  useEffect(() => {
    if (breadcrumbs) {
      setBreadcrumbs(location.pathname, breadcrumbs);
    }
  }, [breadcrumbs, setBreadcrumbs, location.pathname]);
};

export default BreadcrumbProvider;
